import { useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useAtomValue } from 'jotai';
import { ColumnDef } from '@tanstack/react-table';
import { Trash2, TicketPercent, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CURRENCY_OPTIONS } from '@/lib/currencies';
import { useToast } from '@/hooks/useToast';
import { useUpdatePromotionMutationOptions, useDeletePromotionMutationOptions } from '@/lib/query-options';
import { organizationDataAtom } from '@/store';
import type { CampaignData, PromotionType } from '@/lib/types';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

// Mirrors backend/app/campaign/asset_models.py:302 (PromotionOccasion). Stored as the
// human label (lower-case with spaces) — Google Ads' enum maps internally on the backend.
const PROMOTION_OCCASIONS: { value: string; label: string }[] = [
  { value: 'new years', label: 'New Year’s' },
  { value: 'valentines day', label: 'Valentine’s Day' },
  { value: 'easter', label: 'Easter' },
  { value: 'mothers day', label: 'Mother’s Day' },
  { value: 'fathers day', label: 'Father’s Day' },
  { value: 'labor day', label: 'Labor Day' },
  { value: 'back to school', label: 'Back to School' },
  { value: 'halloween', label: 'Halloween' },
  { value: 'black friday', label: 'Black Friday' },
  { value: 'cyber monday', label: 'Cyber Monday' },
  { value: 'christmas', label: 'Christmas' },
  { value: 'boxing day', label: 'Boxing Day' },
  { value: 'independence day', label: 'Independence Day' },
  { value: 'end of season', label: 'End of Season' },
  { value: 'winter sale', label: 'Winter Sale' },
  { value: 'summer sale', label: 'Summer Sale' },
  { value: 'fall sale', label: 'Fall Sale' },
  { value: 'spring sale', label: 'Spring Sale' },
];

const PROMOTION_DISCOUNT_TYPES: { value: 'percent off' | 'money off'; label: string }[] = [
  { value: 'percent off', label: 'Percent off' },
  { value: 'money off', label: 'Money off' },
];

// Currencies in CURRENCY_OPTIONS that don't use decimal subunits (ISO 4217). Amount input
// uses step=1 for these so the stepper bumps by whole units, not 0.01.
const ZERO_DECIMAL_CURRENCIES = new Set(['CLP', 'HUF', 'IDR', 'JPY', 'KRW', 'VND']);

interface PromotionSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

export interface PromotionSectionRef {
  openAdd: () => void;
}

type FormState = {
  promotion_target: string;
  discount_type: 'percent off' | 'money off';
  discount_modifier: string;
  currency: string;
  occasion: string;
  promotion_code: string;
  start_date: string;
  end_date: string;
};

const EMPTY_FORM: FormState = {
  promotion_target: '',
  discount_type: 'percent off',
  discount_modifier: '',
  currency: '',
  occasion: '',
  promotion_code: '',
  start_date: '',
  end_date: '',
};

export const PromotionSection = forwardRef<PromotionSectionRef, PromotionSectionProps>(({ campaign, disabled = false }, ref) => {
  const { toast } = useToast();
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const organizationData = useAtomValue(organizationDataAtom);
  const orgCurrency = organizationData?.default_currency ?? '';
  const promotions = campaign.promotions ?? [];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<PromotionType | null>(null);
  const [deletingPromotion, setDeletingPromotion] = useState<PromotionType | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  type FieldErrors = Partial<Record<'promotion_target' | 'discount_modifier' | 'currency', string>>;
  const [errors, setErrors] = useState<FieldErrors>({});

  const updateMutation = useUpdatePromotionMutationOptions();
  const deleteMutation = useDeletePromotionMutationOptions();

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear the error for this field as the user types so corrections feel responsive.
    if (field in errors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof FieldErrors];
        return next;
      });
    }
  };

  const openAddModal = useCallback(() => {
    setEditingPromotion(null);
    // Pre-fill currency from the organisation's default_currency so the user doesn't have to
    // type USD/EUR/etc every time. They can still override it per-promotion.
    setForm({ ...EMPTY_FORM, currency: orgCurrency });
    setErrors({});
    setIsModalOpen(true);
  }, [orgCurrency]);

  const openEditModal = useCallback((promotion: PromotionType) => {
    setEditingPromotion(promotion);
    setForm({
      promotion_target: promotion.promotion_target,
      discount_type: promotion.discount_type,
      discount_modifier: String(promotion.discount_modifier),
      currency: promotion.currency ?? '',
      occasion: promotion.occasion ?? '',
      promotion_code: promotion.promotion_code ?? '',
      start_date: promotion.start_date ?? '',
      end_date: promotion.end_date ?? '',
    });
    setErrors({});
    setIsModalOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ openAdd: openAddModal }), [openAddModal]);

  const handleSave = async () => {
    // Run validation up front and surface every failure inline on the corresponding field
    // instead of bailing out silently — gives users a clear path to fix things before Save.
    const nextErrors: FieldErrors = {};
    if (!form.promotion_target.trim()) nextErrors.promotion_target = 'Promotion is required.';
    const modifier = parseFloat(form.discount_modifier);
    if (!form.discount_modifier.trim()) nextErrors.discount_modifier = 'Amount is required.';
    else if (Number.isNaN(modifier) || modifier <= 0) nextErrors.discount_modifier = 'Amount must be greater than 0.';
    else if (form.discount_type === 'percent off' && modifier > 100) nextErrors.discount_modifier = 'Percent off cannot exceed 100.';
    if (form.discount_type === 'money off' && !form.currency.trim()) nextErrors.currency = 'Currency is required for money-off promotions.';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const promotion: PromotionType = {
      ...(editingPromotion?.id ? { id: editingPromotion.id } : {}),
      promotion_target: form.promotion_target.trim(),
      discount_type: form.discount_type,
      discount_modifier: modifier,
      currency: form.discount_type === 'money off' ? form.currency.trim().toUpperCase() : null,
      occasion: form.occasion || null,
      promotion_code: form.promotion_code.trim() || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };

    try {
      await updateMutation.mutateAsync({ campaignId: campaign.id, promotion });
      setIsModalOpen(false);
      await refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to save promotion',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPromotion?.id) return;
    try {
      await deleteMutation.mutateAsync({ campaignId: campaign.id, promotionId: deletingPromotion.id });
      setDeletingPromotion(null);
      await refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete promotion',
        variant: 'destructive',
      });
    }
  };

  const formatDiscount = (p: PromotionType) => {
    if (p.discount_type === 'percent off') return `${p.discount_modifier}% off`;
    // Show the currency glyph (€, $, ₩) rather than the ISO code so the row reads naturally.
    // Falls back to the code itself if the currency isn't in CURRENCY_OPTIONS for any reason.
    const symbol = CURRENCY_OPTIONS.find((c) => c.code === p.currency)?.symbol ?? p.currency ?? '';
    return `${symbol}${p.discount_modifier} off`.trim();
  };

  const formatPeriod = (p: PromotionType) => {
    if (!p.start_date && !p.end_date) return '—';
    return `${p.start_date ?? '—'} → ${p.end_date ?? '—'}`;
  };

  const columns = useMemo<ColumnDef<PromotionType, unknown>[]>(() => [
    {
      accessorKey: 'promotion_target',
      header: 'Promotion',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <TicketPercent className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900">{row.original.promotion_target}</div>
            {/* Category and Code share one line so the cell never exceeds two rows (#5812). */}
            {(row.original.occasion || row.original.promotion_code) && (
              <div className="text-xs text-gray-500">
                {row.original.occasion && (
                  <>Category: <span className="capitalize">{row.original.occasion}</span></>
                )}
                {row.original.occasion && row.original.promotion_code && ' · '}
                {row.original.promotion_code && <>Code: {row.original.promotion_code}</>}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'discount_modifier',
      header: 'Discount',
      cell: ({ row }) => (
        <div className="text-sm text-gray-900">{formatDiscount(row.original)}</div>
      ),
    },
    {
      // Sortable by start_date (ISO YYYY-MM-DD strings sort lexically the same as
      // chronologically). Promotions without a start_date sink to the bottom in ascending,
      // float to the top in descending — TanStack's default null handling.
      accessorKey: 'start_date',
      header: 'Period',
      cell: ({ row }) => (
        <div className="text-xs text-gray-500">{formatPeriod(row.original)}</div>
      ),
      sortUndefined: 'last',
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        if (disabled) return null;
        return (
          <div className="flex justify-end gap-1">
            <IconButton
              variant="default"
              onClick={(e) => {
                e.stopPropagation();
                openEditModal(row.original);
              }}
              aria-label="Edit promotion"
            >
              <Pencil className="h-4 w-4" />
            </IconButton>
            <IconButton
              variant="destructive"
              onClick={(e) => {
                e.stopPropagation();
                setDeletingPromotion(row.original);
              }}
              aria-label="Delete promotion"
            >
              <Trash2 className="h-4 w-4" />
            </IconButton>
          </div>
        );
      },
      meta: { className: 'px-6 text-right' },
    },
  ], [disabled, openEditModal]);

  // Only disable Save while the mutation is in flight — otherwise let the user click and
  // see the inline validation errors, instead of staring at a silently-disabled button.
  const isSaveDisabled = updateMutation.isPending;

  return (
    <div>
      <DataTable<PromotionType>
        data={[...promotions].reverse()}
        columns={columns}
        variant="bordered"
        pageSize={999}
        emptyIcon={TicketPercent}
        emptyTitle="No Promotions"
        emptyDescription="Highlight sales, discounts, and promotional offers on your ads."
      />

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editingPromotion ? 'Edit Promotion' : 'Add Promotion'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Promotion target */}
            <div>
              <Label>Promotion</Label>
              <Input
                value={form.promotion_target}
                onChange={(e) => setField('promotion_target', e.target.value)}
                maxLength={20}
                placeholder="e.g. Holiday Sale"
                autoFocus
                className={`mt-1 placeholder:text-gray-300 ${errors.promotion_target ? 'border-red-500' : ''}`}
              />
              {errors.promotion_target && <p className="text-xs mt-0.5 text-red-500">{errors.promotion_target}</p>}
            </div>

            {/* Discount Type / Amount / Currency on one row. Each has its own label.
                Amount keeps a fixed width regardless of whether Currency is rendered, so the
                input doesn't reflow when the discount type changes. Helper / error text lives
                under the Amount cell so it stays anchored to the input it describes. */}
            <div className="flex gap-2 items-start">
              <div>
                <Label>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setField('discount_type', v as 'percent off' | 'money off')}>
                  <SelectTrigger className="w-[140px] h-9 shrink-0 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMOTION_DISCOUNT_TYPES.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount</Label>
                {(() => {
                  // For percent off, always step by 1 whole percent.
                  // For money off, step by 1 if the selected currency has no decimal subunit
                  // (KRW, JPY, etc.), otherwise step by 0.01 for two-decimal currencies.
                  const isZeroDecimal = form.discount_type === 'money off' && ZERO_DECIMAL_CURRENCIES.has(form.currency);
                  const isWholeNumber = form.discount_type === 'percent off' || isZeroDecimal;
                  const isPercent = form.discount_type === 'percent off';
                  return (
                    <div className={`relative mt-1 shrink-0 ${isPercent ? 'w-[140px]' : 'w-[200px]'}`}>
                      {isPercent ? (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">%</span>
                      ) : (
                        <Select value={form.currency || undefined} onValueChange={(v) => setField('currency', v)}>
                          {/* Currency selector sits flush against the left edge of the Amount
                              input — its border is stripped and replaced with a right divider
                              so the pair reads as a single "money + currency" control. Trigger
                              shows the 3-letter ISO code only so it fits with the chevron in
                              the tight slot; full label "EUR (€)" stays in the dropdown menu. */}
                          <SelectTrigger
                            className={`absolute left-0 top-0 z-10 h-9 w-[78px] border-0 border-r border-gray-200 rounded-r-none bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 pl-3 pr-2 text-sm ${errors.currency ? 'text-red-500' : ''}`}
                          >
                            <span className="truncate">{form.currency || '—'}</span>
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCY_OPTIONS.map((c) => (
                              <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        type="text"
                        // type="text" + inputMode keeps the mobile numeric keypad while letting us
                        // preserve trailing zeros in the display (e.g. "5.00" for EUR). `type="number"`
                        // strips trailing zeros, which made the value ambiguous (5 EUR vs 0.05 EUR).
                        inputMode={isWholeNumber ? 'numeric' : 'decimal'}
                        value={form.discount_modifier}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow only digits, plus a single decimal point for two-decimal
                          // currencies. Reject everything else at the keystroke so we don't have
                          // to clean it up on save.
                          const pattern = isWholeNumber ? /^\d*$/ : /^\d*\.?\d{0,2}$/;
                          if (val === '' || pattern.test(val)) {
                            setField('discount_modifier', val);
                          }
                        }}
                        onBlur={() => {
                          // On blur, format to the canonical decimal places for the currency so
                          // "5" + EUR becomes "5.00" — unambiguous five euros, not five cents.
                          if (!form.discount_modifier) return;
                          const num = parseFloat(form.discount_modifier);
                          if (Number.isNaN(num)) return;
                          setField('discount_modifier', isWholeNumber ? String(Math.floor(num)) : num.toFixed(2));
                        }}
                        placeholder={isPercent ? 'e.g. 20' : 'e.g. 5.00'}
                        className={`h-9 w-full placeholder:text-gray-300 ${isPercent ? 'pr-8' : 'pl-[88px]'} ${errors.discount_modifier || errors.currency ? 'border-red-500' : ''}`}
                      />
                    </div>
                  );
                })()}
                {errors.discount_modifier && <p className="text-xs mt-0.5 text-red-500">{errors.discount_modifier}</p>}
                {errors.currency && <p className="text-xs mt-0.5 text-red-500">{errors.currency}</p>}
              </div>
            </div>

            {/* Occasion */}
            <div>
              <Label>Occasion <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Select value={form.occasion} onValueChange={(v) => setField('occasion', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select an occasion" />
                </SelectTrigger>
                <SelectContent>
                  {PROMOTION_OCCASIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Promotion code */}
            <div>
              <Label>Promo Code <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input
                value={form.promotion_code}
                onChange={(e) => setField('promotion_code', e.target.value)}
                maxLength={15}
                placeholder="e.g. HOLIDAY20"
                className="mt-1"
              />
            </div>

            {/* Start / End date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setField('start_date', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setField('end_date', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="leading-none">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaveDisabled}
              className="bg-[#290BD9] hover:bg-[#2309C5] leading-none"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!deletingPromotion}
        onClose={() => setDeletingPromotion(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Promotion"
        description={`Are you sure you want to delete "${deletingPromotion?.promotion_target}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
});
PromotionSection.displayName = 'PromotionSection';
